<script>
  import Section from '../components/Section.svelte';
  import Panel from '../components/Panel.svelte';
  import Alert from '../components/Alert.svelte';
  import Button from '../components/Button.svelte';
  import DataTable from '../components/DataTable.svelte';
  import Async from '../components/Async.svelte';
  import HelpDrawer from '../components/HelpDrawer.svelte';
  import { getPartners, publish } from '../lib/api.js';
  import { HELP } from '../lib/help.js';

  // Re-fetch the registry after a republish so the refreshed publishedAt shows.
  let reloadId = $state(0);
  const partners = $derived.by(() => { reloadId; return getPartners(); });

  let busy = $state(null); // webId currently republishing
  let error = $state(null);
  let notice = $state(null);

  // "Update shared cubes" = republish the partner's granted themes with the
  // current aggregates. Republish is a governance act (the ingest job never
  // touches shared containers), so only then does a new period reach the partner.
  // The registry stores themes as an array; publish() wants the flag map.
  async function updateCubes(p) {
    busy = p.webId; error = null; notice = null;
    try {
      const themes = Object.fromEntries((p.outbound.themes ?? []).map((t) => [t, true]));
      const res = await publish({ webId: p.webId, themes });
      notice = res.ok
        ? `Sent ${p.label} an updated copy of ${res.outbound?.slices.length ?? 0} cube(s).`
        : `Sent ${p.label} an update, but some of it failed — check the catalog.`;
      reloadId += 1;
    } catch (e) {
      error = e.message;
    } finally {
      busy = null;
    }
  }

  const fmtDate = (iso) => (iso ? new Date(iso).toISOString().slice(0, 16).replace('T', ' ') : '—');
  // Outbound container lives on our own Pod, so present = we publish to them.
  const outStatus = (s) => (s === 200 ? 'they can read it' : `not shared yet (${s || 'no response'})`);
  const inStatus = (items) => {
    if (!items?.length) return 'nothing shared yet';
    const readable = items.filter((item) => item.status === 200).length;
    return `${readable} of ${items.length} readable`;
  };
</script>

<HelpDrawer {...HELP.partners} />

<h1 class="wf2-title wf2-title-level-1">Partners</h1>
<p class="wf2-subtitle">
  Manage who this institution shares with. When you take in a new quarter, send
  partners an updated copy — that never happens on its own.
</p>

{#if notice}
  <Alert title="Done" variant="success"><p class="wf2-text">{notice}</p></Alert>
{/if}
{#if error}
  <Alert title="Could not update their copy" variant="danger"><p class="mono">{error}</p></Alert>
{/if}

<Async promise={partners} loading="Reading the partner registry from your Pod…">
  {#snippet children(data)}
    {#if data.partners.length === 0}
      <Section title="No partners yet">
        <Panel>
          <p class="wf2-text">You aren't sharing with anyone yet. Add a partner to let them read a theme.</p>
          <Button variant="primary" to="partner">Add partner</Button>
        </Panel>
      </Section>
    {:else}
      {#each data.partners as p}
        <Section title={p.label}>
          <Panel>
            <DataTable
              columns={['Field', 'Value']}
              rows={[
                ['WebID', p.webId],
                ['Contact', p.contact || '—'],
                ['Themes you share', (p.outbound.themes ?? []).join(', ') || '(none)'],
                ['Cubes you share', `${p.outbound.slices?.length ?? 0}`],
                ['Last sent', fmtDate(p.outbound.publishedAt)],
                ['What you share with them', outStatus(p.outbound.status)],
                ['What they share with you', inStatus(p.inbound.containers)],
              ]}
            />
            {#if p.inbound.containers.length}
              <DataTable
                columns={['What they pointed you at', 'Status']}
                rows={p.inbound.containers.map((item) => [
                  item.label ? `${item.label} — ${item.url}` : item.url,
                  item.status === 200 ? 'you can read it' : [401, 403].includes(item.status) ? `they haven't shared it yet (${item.status})` : `can't reach it (${item.status || 'no response'})`,
                ])}
              />
            {/if}
            <div class="wf2-row" style="gap:12px">
              <Button variant="primary" onclick={() => updateCubes(p)} disabled={busy === p.webId}>
                {busy === p.webId ? 'Sending…' : 'Update shared cubes'}
              </Button>
            </div>
          </Panel>
        </Section>
      {/each}
    {/if}

    <div class="wf2-row" style="gap:12px">
      <Button variant="primary" to="partner">Add partner</Button>
      <Button variant="secondary" to="catalog">Browse catalog</Button>
      <Button variant="secondary" to="dashboard">Back to dashboard</Button>
    </div>
  {/snippet}
</Async>
